import React, { useState, useEffect } from "react";
import { HiOutlineXMark } from "react-icons/hi2";
import masterDataService from "../../../../services/masterDataService";
import { toast } from "react-toastify";
import PageLoading from "../../../../components/common/PageLoading";

const PmTaskAssignmentPanel = ({
  editingTaskResp,
  onClose,
  onUpdate,
  skills,
  teams,
  tenantId,
  selectedAssetId,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");

  const { plan, task } = editingTaskResp || {};

  useEffect(() => {
    if (plan) {
      // Find the current skill ID if possible
      const currentSkill = skills.find((s) => s.skillName === task.responsible);
      setSelectedSkillId(currentSkill ? String(currentSkill.skillId) : "");

      // If we have team info in the task data (passed from service), we would set it
      if (task.assignedTeamId) {
        setSelectedTeamId(String(task.assignedTeamId));
      }
    }
  }, [plan, task, skills]);

  if (!editingTaskResp) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Update 1: Skill on Plan (Global)
      if (selectedSkillId) {
        const fullPlan = await masterDataService.getMaintenancePlan(
          plan.planId,
        );
        fullPlan.requiredSkillId = parseInt(selectedSkillId);
        await masterDataService.updateMaintenancePlan(plan.planId, fullPlan);
      }

      // Update 2: Team on Schedule (Asset-specific)
      if (selectedAssetId && selectedTeamId) {
        // Find the schedule for this plan+asset
        const allSchedules = await masterDataService.getPMSchedules(
          tenantId,
          true,
        );
        const schedule = allSchedules.find(
          (s) =>
            s.maintenancePlanId === plan.planId &&
            s.assetId === parseInt(selectedAssetId),
        );

        if (schedule) {
          schedule.assignedTeamId = parseInt(selectedTeamId);
          await masterDataService.updatePMSchedule(
            schedule.pmScheduleId,
            schedule,
          );
        } else {
          toast.warning("Could not find specific schedule to assign team.");
        }
      }

      toast.success("Responsibility updated successfully");
      onUpdate();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update responsibility");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="task-details-panel">
      {loading && (
        <PageLoading variant="overlay" message="Saving assignment..." />
      )}

      <div className="panel-header">
        <h3>Assign Responsibility</h3>
        <button className="close-panel-btn" onClick={onClose}>
          <HiOutlineXMark />
        </button>
      </div>

      <form onSubmit={handleSave}>
        <div className="panel-section">
          <label>Plan</label>
          <div className="value">{plan.planName}</div>
        </div>

        <div className="panel-section" style={{ display: "none" }}>
          <label>Task</label>
          <div className="value">{task.taskName}</div>
        </div>

        <hr style={{ borderTop: "1px solid #e5e7eb", margin: "12px 0" }} />

        <div className="panel-section">
          <label>Required Skill (Task Level)</label>
          <select
            className="select-input"
            value={selectedSkillId}
            onChange={(e) => setSelectedSkillId(e.target.value)}
          >
            <option value="">Select Skill...</option>
            {skills.map((s) => (
              <option key={s.skillId} value={String(s.skillId)}>
                {s.skillName}
              </option>
            ))}
          </select>
          <p style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
            Updates the maintenance plan globally.
          </p>
        </div>

        {selectedAssetId && (
          <div className="panel-section" style={{ marginTop: "16px" }}>
            <label>Assigned Team (Asset Specific)</label>
            <select
              className="select-input"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              <option value="">Select Team...</option>
              {teams.map((t) => (
                <option key={t.teamId} value={String(t.teamId)}>
                  {t.teamName}
                </option>
              ))}
            </select>
            <p style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
              Assigns a specific team for this equipment.
            </p>
          </div>
        )}

        <div className="action-buttons">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Assignment
          </button>
        </div>
      </form>
    </div>
  );
};

export default PmTaskAssignmentPanel;
